import type { Campaign } from '../../components/campaigns/data/types';
import type { CampaignsAction } from './campaignsActions';
import * as T from './campaignsTypes';
import type { CampaignsState, ResourceState } from './campaignsTypes';

const idle: ResourceState = { loading: false, loaded: false, error: null };
const loading = (prev: ResourceState): ResourceState => ({ ...prev, loading: true, error: null });
const loaded: ResourceState = { loading: false, loaded: true, error: null };
const failed = (prev: ResourceState, error: string): ResourceState => ({ ...prev, loading: false, error });

export const initialState: CampaignsState = {
  campaigns: [],
  campaignsStatus: idle,
  grants: [],
  receivedByCustomer: {},
  redeemedByCustomer: {},
  activityStatus: idle,
  settings: null,
  creditHistory: [],
  creditPacks: [],
  settingsStatus: idle,
  inbound: [],
  inboundStatus: idle,
  sources: [],
  sourcesStatus: idle,
  reports: {},
  recipients: {},
  reportStatus: idle,
  overview: null,
  overviewStatus: idle,
  audiencePreview: null,
  audiencePreviewLoading: false,
  saving: false,
  toasts: [],
};

function upsert<E extends { id: string }>(list: E[], item: E, prepend = false): E[] {
  return list.some((x) => x.id === item.id)
    ? list.map((x) => (x.id === item.id ? item : x))
    : prepend ? [item, ...list] : [...list, item];
}

const upsertCampaign = (list: Campaign[], campaign: Campaign) => upsert(list, campaign, true);

export function campaignsReducer(state: CampaignsState = initialState, action: CampaignsAction): CampaignsState {
  switch (action.type) {
    // ── Audience ──
    case T.PREVIEW_AUDIENCE_REQUEST:
      return { ...state, audiencePreviewLoading: true };
    case T.PREVIEW_AUDIENCE_SUCCESS:
      return { ...state, audiencePreview: action.payload, audiencePreviewLoading: false };
    case T.PREVIEW_AUDIENCE_FAILURE:
      return { ...state, audiencePreviewLoading: false };
    case T.CLEAR_AUDIENCE_PREVIEW:
      return { ...state, audiencePreview: null, audiencePreviewLoading: false };

    // ── Campaigns ──
    case T.FETCH_CAMPAIGNS_REQUEST:
      return action.payload.background ? state : { ...state, campaignsStatus: loading(state.campaignsStatus) };
    case T.FETCH_CAMPAIGNS_SUCCESS:
      return { ...state, campaigns: action.payload, campaignsStatus: loaded };
    case T.FETCH_CAMPAIGNS_FAILURE:
      return { ...state, campaignsStatus: failed(state.campaignsStatus, action.payload) };
    case T.SAVE_CAMPAIGN_SUCCESS:
    case T.DUPLICATE_CAMPAIGN_SUCCESS:
      return { ...state, saving: false, campaigns: upsertCampaign(state.campaigns, action.payload) };
    case T.TRANSITION_CAMPAIGN_SUCCESS: {
      // A new send invalidates the cached report for that campaign.
      const { [action.payload.id]: _report, ...reports } = state.reports;
      const { [action.payload.id]: _rows, ...recipients } = state.recipients;
      return { ...state, saving: false, campaigns: upsertCampaign(state.campaigns, action.payload), reports, recipients };
    }
    case T.DELETE_CAMPAIGN_SUCCESS:
      return { ...state, saving: false, campaigns: state.campaigns.filter((c) => c.id !== action.payload) };
    case T.REVOKE_GRANTS_SUCCESS: {
      const report = state.reports[action.payload];
      return {
        ...state,
        saving: false,
        reports: report ? { ...state.reports, [action.payload]: { ...report, unredeemedGrants: 0 } } : state.reports,
        grants: state.grants.map((g) => (g.campaignId === action.payload && g.status === 'active' ? { ...g, status: 'revoked' as const } : g)),
      };
    }

    // ── Report ──
    case T.FETCH_REPORT_REQUEST:
      return { ...state, reportStatus: loading(state.reportStatus) };
    case T.FETCH_REPORT_SUCCESS:
      return {
        ...state,
        reportStatus: loaded,
        reports: { ...state.reports, [action.payload.campaignId]: action.payload.report },
        recipients: { ...state.recipients, [action.payload.campaignId]: action.payload.recipients },
      };
    case T.FETCH_REPORT_FAILURE:
      return { ...state, reportStatus: failed(state.reportStatus, action.payload) };

    // ── Rewards and campaign history per customer ──
    case T.FETCH_ACTIVITY_REQUEST:
      return { ...state, activityStatus: loading(state.activityStatus) };
    case T.FETCH_ACTIVITY_SUCCESS:
      return { ...state, ...action.payload, activityStatus: loaded };
    case T.FETCH_ACTIVITY_FAILURE:
      return { ...state, activityStatus: failed(state.activityStatus, action.payload) };

    // ── Settings ──
    case T.FETCH_SETTINGS_REQUEST:
      return { ...state, settingsStatus: loading(state.settingsStatus) };
    case T.FETCH_SETTINGS_SUCCESS:
      return { ...state, saving: false, settings: action.payload.settings, creditHistory: action.payload.creditHistory, creditPacks: action.payload.creditPacks, settingsStatus: loaded };
    case T.FETCH_SETTINGS_FAILURE:
      return { ...state, settingsStatus: failed(state.settingsStatus, action.payload) };
    case T.SAVE_SETTINGS_SUCCESS:
      return { ...state, saving: false, settings: action.payload };

    // ── Opt-out review queue ──
    case T.FETCH_INBOUND_REQUEST:
      return { ...state, inboundStatus: loading(state.inboundStatus) };
    case T.FETCH_INBOUND_SUCCESS:
      return { ...state, inbound: action.payload, inboundStatus: loaded };
    case T.FETCH_INBOUND_FAILURE:
      return { ...state, inboundStatus: failed(state.inboundStatus, action.payload) };
    case T.RESOLVE_INBOUND_SUCCESS:
      return { ...state, saving: false, inbound: upsert(state.inbound, action.payload.inbound) };

    // ── Tracking sources ──
    case T.FETCH_SOURCES_REQUEST:
      return { ...state, sourcesStatus: loading(state.sourcesStatus) };
    case T.FETCH_SOURCES_SUCCESS:
      return { ...state, sources: action.payload, sourcesStatus: loaded };
    case T.FETCH_SOURCES_FAILURE:
      return { ...state, sourcesStatus: failed(state.sourcesStatus, action.payload) };
    case T.SAVE_SOURCE_SUCCESS:
      return { ...state, saving: false, sources: upsert(state.sources, action.payload, true) };
    case T.DELETE_SOURCE_SUCCESS:
      return { ...state, saving: false, sources: state.sources.filter((s) => s.id !== action.payload) };

    // ── Overview ──
    case T.FETCH_OVERVIEW_REQUEST:
      return { ...state, overviewStatus: loading(state.overviewStatus) };
    case T.FETCH_OVERVIEW_SUCCESS:
      return { ...state, overview: action.payload, overviewStatus: loaded };
    case T.FETCH_OVERVIEW_FAILURE:
      return { ...state, overviewStatus: failed(state.overviewStatus, action.payload) };

    // ── Mutations in flight ──
    case T.SAVE_CAMPAIGN_REQUEST:
    case T.TRANSITION_CAMPAIGN_REQUEST:
    case T.DUPLICATE_CAMPAIGN_REQUEST:
    case T.DELETE_CAMPAIGN_REQUEST:
    case T.REVOKE_GRANTS_REQUEST:
    case T.SAVE_SETTINGS_REQUEST:
    case T.BUY_CREDITS_REQUEST:
    case T.RESOLVE_INBOUND_REQUEST:
    case T.SAVE_SOURCE_REQUEST:
    case T.DELETE_SOURCE_REQUEST:
      return { ...state, saving: true };
    case T.MUTATION_FAILURE:
      return { ...state, saving: false };

    // ── Toasts ──
    case T.TOAST_ADD:
      return { ...state, toasts: [...state.toasts, action.payload] };
    case T.TOAST_REMOVE:
      return { ...state, toasts: state.toasts.filter((t) => t.id !== action.payload) };

    default:
      return state;
  }
}
